import React, { useState } from 'react';
import {
  Instagram,
  Linkedin,
  Youtube,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import PatreonIcon from '@/components/icons/PatreonIcon';
import { BIO } from '@/constants/testIds';

// Smooth micro-interactions (Tailwind) applied to every link block, plus a
// conditional pulsing cream-colored border when the block matches the
// detected ingress origin.
const BASE_TRANSITION = 'transition-all duration-300 ease-in-out';
const HIGHLIGHT_CLASSES =
  'border-2 border-[#F5F5F3] animate-pulse shadow-[0_0_0_2px_rgba(245,245,243,0.6)]';

/**
 * Resolve a platform name to an icon component. Used by collapsible groups.
 */
const PLATFORM_ICONS = {
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  patreon: PatreonIcon,
  lchaim: Sparkles,
};

const PlatformIcon = ({ platform }) => {
  const Icon = PLATFORM_ICONS[platform];
  if (!Icon) return null;
  return (
    <span className="lib-sublink-icon" aria-hidden="true">
      <Icon size={16} strokeWidth={1.75} />
    </span>
  );
};

/**
 * Renders a single plain anchor button.
 */
const LinkAnchor = ({ link, highlighted }) => (
  <a
    className={`lib-link-button ${BASE_TRANSITION} ${
      highlighted ? HIGHLIGHT_CLASSES : ''
    }`}
    href={link.url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={link.testId}
    data-highlighted={highlighted ? 'true' : 'false'}
  >
    <span className="lib-link-button-label">{link.label}</span>
    <span className="lib-link-button-arrow" aria-hidden="true">
      &rarr;
    </span>
  </a>
);

/**
 * Renders a collapsible group whose header (the button) opens a panel of
 * sub-links beneath it. Sub-links open in a new tab.
 */
const LinkGroup = ({ group, highlighted }) => {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((prev) => !prev);

  return (
    <div className={`lib-link-group${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className={`lib-link-button lib-link-group-toggle ${BASE_TRANSITION} ${
          highlighted ? HIGHLIGHT_CLASSES : ''
        }`}
        onClick={toggle}
        data-testid={group.toggleTestId}
        aria-expanded={open}
        aria-controls={group.panelTestId}
        data-highlighted={highlighted ? 'true' : 'false'}
      >
        <span className="lib-link-button-label">{group.label}</span>
        <span
          className={`lib-link-group-chevron${open ? ' is-open' : ''}`}
          aria-hidden="true"
        >
          <ChevronDown size={16} strokeWidth={1.75} />
        </span>
      </button>

      {open && (
        <div
          id={group.panelTestId}
          className="lib-sublink-panel"
          data-testid={group.panelTestId}
          role="region"
          aria-label={`${group.label} links`}
        >
          {group.subLinks.map((sub) => (
            <a
              key={sub.testId}
              className={`lib-sublink ${BASE_TRANSITION}`}
              href={sub.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={sub.testId}
            >
              <PlatformIcon platform={sub.platform} />
              <span className="lib-sublink-meta">
                <span className="lib-sublink-platform">{sub.platformLabel}</span>
                <span className="lib-sublink-handle">{sub.handle}</span>
              </span>
              <span className="lib-sublink-arrow" aria-hidden="true">
                &rarr;
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * LinkButtonStack — vertical stack of link items. Each item is either a
 * plain anchor (`type: 'link'`) or a collapsible group (`type: 'group'`).
 *
 * When `highlightedTestId` matches an item's testId, that block receives a
 * pulsing cream-colored border to force visual prioritization (driven by the
 * host-aware ingress origin).
 */
const LinkButtonStack = ({ links, highlightedTestId }) => {
  return (
    <nav
      className="lib-link-stack"
      data-testid={BIO.linkStack}
      aria-label="External links"
    >
      {links.map((item) => {
        if (item.type === 'group') {
          const isHighlighted = highlightedTestId === item.toggleTestId;
          return (
            <LinkGroup
              key={item.toggleTestId}
              group={item}
              highlighted={isHighlighted}
            />
          );
        }
        const isHighlighted = highlightedTestId === item.testId;
        return (
          <LinkAnchor
            key={item.testId}
            link={item}
            highlighted={isHighlighted}
          />
        );
      })}
    </nav>
  );
};

export default LinkButtonStack;
